import * as React from "react";
import type { Voice } from "@fapinstructor/common";
import { useAuth } from "@clerk/clerk-react";
import {
  ActionIcon,
  Group,
  Select,
  Stack,
  Accordion,
  Text,
  Tooltip,
  Box,
  Center,
} from "@mantine/core";
import { triggerQueryOptions } from "@/api/fapinstructor/triggers/get-triggers";
import {
  TbCheck,
  TbChevronLeft,
  TbChevronRight,
  TbCircle,
  TbCircleFilled,
  TbInfoCircle,
  TbTestPipe,
} from "react-icons/tb";
import { useTranslation } from "react-i18next";
import { InstructorSelectInput } from "@/components/instructor-select-input";
import { useHasScriptFeatureAccess } from "@/hooks/use-has-script-feature-access";
import { ScriptUpgradeMembershipTierBanner } from "@/components/script/script-upgrade-membership-tier-banner";
import { scriptQueryOptions } from "@/api/fapinstructor/script/get-script";
import { createFileRoute } from "@tanstack/react-router";
import { TriggerPanel } from "./-components/trigger-panel";

export const Route = createFileRoute(
  "/_authenticated/_subscribed/script/$scriptId/dialog",
)({
  loader: async (opts) => {
    const script = await opts.context.queryClient.ensureQueryData(
      scriptQueryOptions({ scriptId: opts.params.scriptId }),
    );
    const triggers = await opts.context.queryClient.ensureQueryData(
      triggerQueryOptions({ scriptId: opts.params.scriptId }),
    );
    return { script, triggers };
  },

  component: RouteComponent,
});

function RouteComponent() {
  const { script, triggers } = Route.useLoaderData();
  const hasScriptFeatureAccess = useHasScriptFeatureAccess();
  const { userId } = useAuth();
  const isAuthor = userId === script.createdBy.id;
  const editable = isAuthor && hasScriptFeatureAccess;
  const [triggerIndex, setTriggerIndex] = React.useState(5);
  const { t } = useTranslation();
  const [voice, setVoice] = React.useState<Voice | undefined>("chloe");

  return (
    <>
      {isAuthor && !hasScriptFeatureAccess ? (
        <Box mb="lg">
          <ScriptUpgradeMembershipTierBanner />
        </Box>
      ) : null}
      <Stack>
        <Accordion variant="contained" disableChevronRotation>
          <Accordion.Item value="test-controls">
            <Accordion.Control icon={<TbTestPipe />}>
              Test Controls
            </Accordion.Control>
            <Accordion.Panel>
              <InstructorSelectInput
                label="Select an instructor to test different audio clips."
                value={voice}
                onChange={(voice) => {
                  setVoice(voice as Voice);
                }}
              />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
        <Group wrap="nowrap" align="flex-end" gap="xs">
          <ActionIcon
            aria-label="Previous trigger"
            disabled={triggerIndex <= 0}
            size="xl"
            onClick={() => {
              setTriggerIndex((i) => i - 1);
            }}
          >
            <TbChevronLeft />
          </ActionIcon>
          <Select
            w="100%"
            size="md"
            label={
              <Group gap="0.5ch">
                <Text size="md">Select a trigger</Text>
                <Tooltip label="Triggers are invoked when different tasks or conditions are met.">
                  <Center inline>
                    <TbInfoCircle size="1.2rem" />
                  </Center>
                </Tooltip>
              </Group>
            }
            data={triggers
              .map(({ id }) => ({
                value: id,
                label: t(id, { ns: "triggers" }),
              }))
              .sort((a, b) => a.value.localeCompare(b.value))}
            value={triggers[triggerIndex].id}
            onChange={(value) => {
              const index = triggers.findIndex(
                (trigger) => trigger.id === value,
              );
              setTriggerIndex(index);
            }}
            searchable
            allowDeselect={false}
            maxDropdownHeight={600}
            renderOption={({ option, checked }) => {
              const dialogCount =
                triggers.find((t) => t.id === option.value)?.dialogCount ?? 0;

              return (
                <Group flex="1" gap="xs">
                  {dialogCount > 0 ? (
                    <TbCircleFilled
                      size="0.5rem"
                      color="var(--mantine-color-green-filled)"
                    />
                  ) : (
                    <TbCircle size="0.5rem" />
                  )}
                  {option.label}
                  {checked && <TbCheck style={{ marginInlineStart: "auto" }} />}
                </Group>
              );
            }}
          />
          <ActionIcon
            aria-label="Next trigger"
            size="xl"
            disabled={triggerIndex >= triggers.length - 1}
            onClick={() => {
              setTriggerIndex((i) => i + 1);
            }}
          >
            <TbChevronRight />
          </ActionIcon>
        </Group>
        <TriggerPanel
          scriptId={script.id}
          trigger={triggers[triggerIndex].id}
          editable={editable}
          voice={voice}
          published={script.published}
        />
      </Stack>
    </>
  );
}
